import { useEffect, useRef } from "react";
import { Asterisk } from "lucide-react";
import "./setuponce.css";

// Beat 4, the peak: "Set up once. Run it the way you want." as a stacking card
// deck (BRIEF-liquid-glass.md 5d, from Dom's three fora frames). Four whole
// cards pile up one on top of the next: each sticks a little lower than the
// one before, so the covered card keeps its top edge and its label showing
// while it dims and recedes. By the end the deck is a pile with only the last
// card fully readable, and then the section releases.
//
// Everything is driven from ONE progress value per frame (see the effect
// below). Phones under 900px and any reduced-motion visitor get the same four
// cards as plain glass sections, panel under copy, nothing sticky.

const N = 4;
// How far into a card's arrival the recession starts. Below this the incoming
// card is still travelling and has not reached the one beneath it yet, so
// dimming early reads as the page flickering rather than as depth.
const DEAD = 0.22;
// The pile stops deepening after two cards: a third step would shrink the
// bottom card far enough to look like a different design.
const MAX_DEPTH = 2;

// x (0 to 1) to an eased 0 to 1 with the dead zone taken off the front.
function unit(x: number) {
  const t = Math.min(1, Math.max(0, (x - DEAD) / (1 - DEAD)));
  return t * t * (3 - 2 * t);
}

// raw = how many cards have landed on top of this one, fractionally.
// Returns the same number eased and capped, which is what drives the lift,
// the shrink and the dim.
export function depthFrom(raw: number) {
  if (raw <= 0) return 0;
  if (raw >= MAX_DEPTH) return MAX_DEPTH;
  const whole = Math.floor(raw);
  return Math.min(MAX_DEPTH, whole + unit(raw - whole));
}

// ------------------------------------------------------------------ panels
// Our own markup, never a screenshot of anyone else's product, every panel
// labelled Illustrative. Sample name and slot times are the ones the site
// already uses.

function CallPanel() {
  return (
    <figure
      className="deck-portrait"
      style={{
        "--deck-mars": "url(/photos/home/mars-glass-background.webp)",
      } as React.CSSProperties}
    >
      <div className="deck-portrait__glass">
        <img
          src="/photos/home/shop-owner-glass.webp"
          alt="A shop owner greeting a customer beside a phone at her counter"
          width="1400"
          height="1400"
          loading="lazy"
          decoding="async"
        />
      </div>
    </figure>
  );
}

function CalendarPanel() {
  return (
    <figure
      className="deck-portrait"
      style={{
        "--deck-mars": "url(/photos/home/mars-glass-background-2.webp)",
      } as React.CSSProperties}
    >
      <div className="deck-portrait__glass">
        <img
          src="/photos/home/owner-calendar-glass.webp"
          alt="A business owner marking a job on the wall calendar behind his counter"
          width="1400"
          height="1400"
          loading="lazy"
          decoding="async"
        />
      </div>
    </figure>
  );
}


function RecordPanel() {
  return (
    <figure
      className="deck-portrait"
      style={{
        "--deck-mars": "url(/photos/home/mars-glass-background-3.webp)",
      } as React.CSSProperties}
    >
      <div className="deck-portrait__glass">
        <img
          src="/photos/home/owner-notebook-glass.webp"
          alt="A business owner writing notes in a notebook at her desk beside her phone"
          width="1400"
          height="1400"
          loading="lazy"
          decoding="async"
        />
      </div>
    </figure>
  );
}


function FollowUpPanel() {
  return (
    <figure
      className="deck-portrait"
      style={{
        "--deck-mars": "url(/photos/home/mars-glass-background-4.webp)",
      } as React.CSSProperties}
    >
      <div className="deck-portrait__glass">
        <img
          src="/photos/home/follow-up-customer-glass.webp"
          alt="A customer smiling as she reads a follow-up message on her phone"
          width="1400"
          height="1400"
          loading="lazy"
          decoding="async"
        />
      </div>
    </figure>
  );
}

// ------------------------------------------------------------------- cards

const CARDS = [
  {
    id: "front-desk",
    label: "Your front desk",
    heading: ["It answers", "in your words."],
    body: "Write the greeting, the questions and the words your business actually uses. Chleb asks them in that order, on every call, at any hour.",
    take: "The first hello, in your words.",
    Panel: CallPanel,
  },
  {
    id: "calendar",
    label: "Your calendar",
    heading: ["Bookings land", "under your rules."],
    body: "Connect the calendar you already run. Chleb offers the slots your rules allow and nothing else, and your team confirms the visit before any work starts.",
    take: "Your hours, your rules.",
    Panel: CalendarPanel,
  },
  {
    id: "customers",
    label: "Your customers",
    heading: ["Every call becomes", "a record."],
    body: "The caller, the reason they rang and the notes your team needs land in one record. It opens in your CRM with the call, the messages and the booking on a single timeline.",
    take: "The details, kept.",
    Panel: RecordPanel,
  },
  {
    id: "follow-up",
    label: "Your follow-up",
    heading: ["Nothing goes cold."],
    body: "A booked caller gets the confirmation. A call that got away gets a message back with a way into the calendar. Both sit on the same timeline as the call itself.",
    take: "The next step, already moving.",
    Panel: FollowUpPanel,
  },
] as const;

export function SetUpOnce() {
  const section = useRef<HTMLElement>(null);
  const track = useRef<HTMLDivElement>(null);
  // Written by the effect, read by the focus handler, so tabbing into a
  // covered card knows where to scroll to.
  const landing = useRef<number[]>([]);
  const stacking = useRef(false);

  useEffect(() => {
    const root = section.current;
    const rail = track.current;
    if (!root || !rail || typeof window.matchMedia !== "function") return;
    // The one gate. It matches the media query in setuponce.css exactly: below
    // 900px, or with reduced motion asked for, the cards are plain sections
    // and nothing here runs.
    const mq = window.matchMedia(
      "(min-width: 900px) and (prefers-reduced-motion: no-preference)",
    );
    const slots = Array.from(
      rail.querySelectorAll<HTMLElement>(".deck-slot"),
    );
    const cards = slots.map(
      (s) => s.firstElementChild as HTMLElement | null,
    );
    if (slots.length !== N || cards.some((c) => !c)) return;

    let raf = 0;
    let near = false;
    let start = 0;
    let span = 0;

    // Two forced layouts, on entry and on resize only, never per frame. The
    // sticky offset has to be read while the slots are still sticky, and the
    // flow positions have to be read while they are not, so the read happens
    // in two passes with the deck momentarily un-stuck in between. Nothing
    // paints in between: it is one synchronous task.
    const measure = () => {
      const tops = slots.map(
        (s) => parseFloat(getComputedStyle(s).top) || 0,
      );
      rail.classList.add("is-measuring");
      const y = window.scrollY;
      const flow = slots.map((s) => s.getBoundingClientRect().top + y);
      rail.classList.remove("is-measuring");
      // Where each card lands and stops. Because the cards are the same
      // height and the gaps between them are equal, these are evenly spaced,
      // which is what lets a single 0-to-1 value drive all four.
      landing.current = flow.map((f, i) => f - tops[i]);
      start = landing.current[0];
      span = landing.current[N - 1] - start;
    };

    // One read of scroll position, one derived progress value, four writes.
    const paint = () => {
      raf = 0;
      const p =
        span > 0
          ? Math.min(1, Math.max(0, (window.scrollY - start) / span))
          : 0;
      for (let i = 0; i < N; i += 1) {
        cards[i]!.style.setProperty(
          "--d",
          depthFrom(p * (N - 1) - i).toFixed(4),
        );
      }
    };

    const onScroll = () => {
      if (near && !raf) raf = requestAnimationFrame(paint);
    };
    const remeasure = () => {
      if (!stacking.current) return;
      measure();
      onScroll();
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        near = entry.isIntersecting;
        if (near) remeasure();
      },
      { rootMargin: "120% 0px" },
    );

    const apply = () => {
      stacking.current = mq.matches;
      if (!mq.matches) {
        near = false;
        observer.disconnect();
        cards.forEach((c) => c!.style.removeProperty("--d"));
        return;
      }
      observer.observe(root);
      measure();
      paint();
    };
    apply();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", remeasure);
    mq.addEventListener("change", apply);
    const ro = new ResizeObserver(remeasure);
    ro.observe(root);
    if (document.fonts?.ready) void document.fonts.ready.then(remeasure);

    return () => {
      observer.disconnect();
      ro.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", remeasure);
      mq.removeEventListener("change", apply);
      cancelAnimationFrame(raf);
    };
  }, []);

  // A covered card is on screen but behind the cards that landed on it, so
  // the browser's own focus scrolling has nothing to do and the reader would
  // be typing into something they cannot see. Only the page knows the scroll
  // position at which a given card is the readable one, so it parks there.
  const reachCard = (index: number) => (event: React.FocusEvent) => {
    if (!stacking.current || event.target !== event.currentTarget) return;
    const at = landing.current[index];
    if (at === undefined) return;
    // Already parked on this card: leave the reader where they are.
    if (Math.abs(window.scrollY - at) < 6) return;
    window.scrollTo({
      top: at + 2,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  };

  return (
    <section
      className="deck g-tone g-tone--deep"
      id="set-up-once"
      ref={section}
    >
      <div className="rb-wrap deck-head" data-sc-in data-sc-stagger="70">
        <span className="g-eyebrow">What you get</span>
        <h2>
          Set up once.
          <br />
          Run it the way you want.
        </h2>
        <p className="g-lede">
          Chleb is built around the way your front office already works, then it
          stays out of the way. From the first greeting to the hundredth
          booking, the rules stay yours.
        </p>
      </div>

      <div className="rb-wrap deck-track" ref={track}>
        {CARDS.map((card, i) => (
          <div
            className="deck-slot"
            key={card.id}
            style={{ "--i": i } as React.CSSProperties}
          >
            <article
              className="deck-card"
              tabIndex={0}
              aria-labelledby={`deck-${card.id}`}
              onFocus={reachCard(i)}
            >
              <div className="deck-card__copy">
                <span className="deck-dot">{card.label}</span>
                <h3 className="g-h3" id={`deck-${card.id}`}>
                  {card.heading.map((line, l) => (
                    <span key={line}>
                      {line}
                      {l < card.heading.length - 1 ? <br /> : null}
                    </span>
                  ))}
                </h3>
                <p className="deck-body">{card.body}</p>
                <p className="deck-take">
                  <Asterisk size={15} aria-hidden="true" />
                  {card.take}
                </p>
              </div>
              <div className="deck-card__panel">
                <card.Panel />
              </div>
              <div className="deck-card__dim" aria-hidden="true" />
            </article>
          </div>
        ))}
        <div className="deck-tail" aria-hidden="true" />
      </div>
    </section>
  );
}
